import { useCallback, useContext } from 'react';
import ErrorBoundaryResettable from 'components/Kizen/ErrorBoundaryResettable';
import Dashlet from './index';
import DashletError from '../DashletError';
import { DashletMenuProvider } from './DashletMenuContext';
import { useQueryClient } from '@tanstack/react-query';
import { ENTITY_TYPES, REPORT_TYPES } from 'components/Wizards/Dashlet/types';
import { isPluginContentDashlet } from 'components/DashboardGrid/hooks/usePluginContentBlock';
import { editableDashlet, getDashletQueryKey } from 'pages/Dashboard/util';
import { DASHBOARD } from 'queries/query-keys';
import PersistentState from 'pages/Dashboard/context/persistentState';
import { useManagedQueryRefetch } from 'pages/Dashboard/context/dataManager/useManagedQueryRefetch';
import { useHTMLConfig } from 'pages/Homepages/hooks/useHTMLConfig';

const ResettableDashlet = ({
  currentDashboard,
  dashlet,
  deleteDashlet,
  clientObjectId,
  charts,
  errorPadding,
  homepages,
  setContentHeight,
  index,
  // Homepages pass this down explicitly; dashboards/chart groups don't, so we
  // derive it from the dashlet below to keep static-content rendering working
  // on every surface.
  builderContent: passedBuilderContent,
  ...rest
}) => {
  const { builderContent: derivedBuilderContent } = useHTMLConfig(dashlet);

  const isPluginBlock = isPluginContentDashlet(dashlet);

  // Plugin blocks are self-contained in PluginBlockDashlet — they don't use
  // the builderContent channel.
  const builderContent =
    passedBuilderContent ?? (isPluginBlock ? undefined : derivedBuilderContent);

  const {
    teamFilter,
    editDashlet,
    model,
    canEdit,
    mobile,
    onShowEditModal,
    showDateShortcut,
    todayDateInBusinessTimeZone,
    ranges,
  } = rest;
  let { dateFilter } = rest;

  if (!dateFilter && dashlet?.config?.feExtraInfo?.dateFilter) {
    dateFilter = dashlet.config.feExtraInfo.dateFilter;
  }

  const { dispatch, state } = useContext(PersistentState.Context);
  const currentEmail = state?.[dashlet.id];
  const managedRefetch = useManagedQueryRefetch();

  const queryClient = useQueryClient();

  const handleRefreshDashlet = useCallback(
    (bustCache = false, isError = true) => {
      const isEmailInteractionStats =
        dashlet?.config?.reportType === REPORT_TYPES.INTERACTION_STATS;

      // Need to reset any saved state for this dashlet in order to do a fresh
      // refetch of the data and avoid whatever error was encountered, if we're in
      // an error state
      if (isError) {
        dispatch({ [dashlet.id]: null });
      }

      let queryKey;

      if (isEmailInteractionStats) {
        queryKey = getDashletQueryKey(
          {
            dashletData: dashlet,
            dateFilter,
            teamFilter,
            currentEmail: currentEmail?.id,
          },
          {
            ignoreObjectId: homepages,
            ignoreFilters: homepages,
          }
        );
      } else if (
        dashlet?.config?.entityType !== ENTITY_TYPES.ACTIVITY ||
        dashlet?.config?.reportType ===
          REPORT_TYPES.NUMBER_OF_ACTIVITY_SUBMISSIONS
      ) {
        queryKey = getDashletQueryKey(
          { dashletData: dashlet, dateFilter, teamFilter },
          {
            ignoreObjectId: homepages,
            ignoreFilters: homepages,
          }
        );
      } else if (
        dashlet?.config?.entityType === 'activity' &&
        dashlet?.config?.reportType !== REPORT_TYPES.SCHEDULED_ACTIVITIES
      ) {
        queryKey = [...DASHBOARD.DASHLET_ACTIVITY, dashlet?.config?.objectId];
      }

      if (
        dashlet?.config?.entityType === ENTITY_TYPES.ACTIVITY &&
        dashlet?.config?.reportType !== REPORT_TYPES.SCHEDULED_ACTIVITIES
      ) {
        queryClient.refetchQueries({
          queryKey: [...DASHBOARD.DASHLET_ACTIVITY, dashlet?.config?.objectId],
        });
      }

      if (queryKey) {
        managedRefetch({ dashletId: dashlet.id, bustCache, queryKey });

        if (!bustCache) {
          queryClient.refetchQueries({ queryKey });
        }
      }
    },
    [
      dashlet,
      queryClient,
      dateFilter,
      teamFilter,
      dispatch,
      homepages,
      managedRefetch,
      currentEmail,
    ]
  );

  const handleBustCache = useCallback(() => {
    return handleRefreshDashlet(true, false);
  }, [handleRefreshDashlet]);

  const handleChangeColumnWidths = useCallback(
    (widths) => {
      editDashlet(
        editableDashlet({
          ...dashlet,
          config: {
            ...dashlet.config,
            feExtraInfo: {
              ...dashlet.config.feExtraInfo,
              columnWidths: widths,
            },
          },
        }),
        undefined,
        true // Skip dashboard refetch for column width updates
      );
    },
    [dashlet, editDashlet]
  );

  return (
    <ErrorBoundaryResettable
      FallbackComponent={({ error, onReset }) => {
        const errorCanEdit = canEdit && (homepages ? !mobile : true); // disable for mobile homepages

        return (
          <DashletMenuProvider canEdit={errorCanEdit}>
            <DashletError
              error={error}
              onDelete={() => deleteDashlet(currentDashboard.id, dashlet.id)}
              onRefresh={() => {
                onReset();
                handleRefreshDashlet();
              }}
              showDateShortcut={showDateShortcut}
              todayDateInBusinessTimeZone={todayDateInBusinessTimeZone}
              mobile={mobile}
              editDashlet={editDashlet}
              ranges={ranges}
              onShowEditModal={(...args) =>
                onShowEditModal({
                  ...args,
                  isErrorBoundary: true,
                  onRefresh: () => {
                    onReset();
                    handleRefreshDashlet();
                  },
                })
              }
              dashlet={dashlet}
              clientObjectId={clientObjectId}
              model={model}
              charts={charts}
              canEdit={errorCanEdit}
              padding={errorPadding}
            />
          </DashletMenuProvider>
        );
      }}
    >
      <Dashlet
        dashboardId={currentDashboard.id}
        defaultStyleConfig={currentDashboard?.styleSettings}
        dashlet={dashlet}
        charts={charts}
        onChangeColumnWidths={handleChangeColumnWidths}
        {...rest}
        builderContent={builderContent}
        dateFilter={dateFilter}
        homepages={homepages}
        setContentHeight={setContentHeight}
        index={index}
        handleBustCache={handleBustCache}
      />
    </ErrorBoundaryResettable>
  );
};

export default ResettableDashlet;
